import React from 'react';
import { Instagram, Youtube, Apple, ArrowRight } from 'lucide-react';
import { NAV_LINKS, SOCIALS } from '../mock';
import { SpotifyIcon } from './icons';
import { useSiteNav } from '../hooks/use-site-nav';

const LEGAL_LINKS = [
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Contact', href: '/contact' },
];

const SOCIAL_ICONS = { instagram: Instagram, youtube: Youtube, apple: Apple };

const SocialIcon = ({ id, size = 13 }) => {
  const Icon = SOCIAL_ICONS[id];
  return Icon ? <Icon size={size} strokeWidth={1.5} /> : <SpotifyIcon size={size} />;
};

const FooterSocial = () => (
  <div className="mt-6 flex items-center gap-3">
    {SOCIALS.map((s) => (
      <a
        key={s.id}
        href={s.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={s.label}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-white/20 text-neutral-400 transition-colors duration-300 hover:border-gold hover:text-gold"
      >
        <SocialIcon id={s.id} />
      </a>
    ))}
  </div>
);

const FooterBrand = ({ go }) => (
  <div className="max-w-sm">
    <a href="/" onClick={(e) => go(e, '/')} aria-label="KHM — Kanwal Malik, home" className="inline-block">
      <img src="/assets/khm-logo.png" alt="KHM — Kanwal Malik" className="h-16 w-auto" />
    </a>
    <p className="mt-5 text-base leading-relaxed text-neutral-500">
      Entrepreneur, humanitarian and artist — using music as a voice for something greater.
    </p>
    <FooterSocial />
  </div>
);

const FooterLinkGroup = ({ title, links, go }) => (
  <div>
    <p className="mb-5 text-[10px] tracking-[0.3em] text-gold">{title}</p>
    <nav className="flex flex-col gap-3">
      {links.map((link) => (
        <a
          key={link.label}
          href={link.href}
          onClick={(e) => go(e, link.href)}
          className="text-[11px] tracking-[0.18em] text-neutral-400 transition-colors duration-300 hover:text-gold"
        >
          {link.label}
        </a>
      ))}
    </nav>
  </div>
);

const FooterLinks = ({ go }) => (
  <div className="flex flex-wrap gap-x-16 gap-y-10">
    <FooterLinkGroup title="EXPLORE" links={NAV_LINKS} go={go} />
    <FooterLinkGroup title="LEGAL" links={LEGAL_LINKS} go={go} />
  </div>
);

const FooterBooking = ({ go }) => (
  <div className="max-w-xs">
    <p className="mb-5 text-[10px] tracking-[0.3em] text-gold">BOOKINGS</p>
    <p className="text-base leading-relaxed text-neutral-500">
      For concerts, corporate events, private functions and collaborations.
    </p>
    <a
      href="/contact"
      onClick={(e) => go(e, '/contact')}
      className="group mt-5 inline-flex items-center gap-2.5 border border-white/30 px-6 py-3 text-[10px] tracking-[0.25em] text-white transition-colors duration-300 hover:border-gold hover:text-gold"
    >
      BOOK KHM
      <ArrowRight size={11} className="transition-transform duration-300 group-hover:translate-x-0.5" />
    </a>
  </div>
);

const FooterCopyright = () => (
  <div className="mt-14 flex flex-col items-center justify-between gap-4 border-t border-white/5 pt-7 md:flex-row">
    <p className="text-[10px] tracking-[0.15em] text-neutral-400">
      © {new Date().getFullYear()} KHM. All Rights Reserved.
    </p>
    <p className="text-[10px] tracking-[0.15em] text-neutral-400">Music that moves. A voice that connects.</p>
  </div>
);

const Footer = () => {
  const { navTo } = useSiteNav();
  const go = (e, href) => {
    e.preventDefault();
    navTo(href);
  };

  return (
    <footer className="border-t border-white/10 bg-ink">
      <div className="mx-auto max-w-[1400px] px-6 py-14 md:px-10">
        <div className="flex flex-col gap-12 lg:flex-row lg:items-start lg:justify-between">
          <FooterBrand go={go} />
          <FooterLinks go={go} />
          <FooterBooking go={go} />
        </div>
        <FooterCopyright />
      </div>
    </footer>
  );
};

export default Footer;
